import { useEffect, useState } from 'react'

import { useGetSingleTopicProgress } from 'api/microlearning/useAPIProgress'
import {
  useGetAksiNyataRevision,
  useGetTopicDetail,
} from 'api/microlearning/useAPITopics'

import type { CertificateStatusType } from 'api/microlearning/types'
import type {
  Module,
  TopicDetails,
  OverallProgress,
  TAPIGetAksiNyataRevisionResponse,
  ModuleProgress,
} from 'types/microlearning'

type FetchStatus = 'idle' | 'loading' | 'success' | 'error'

export interface GetTopicDetails {
  fetchStatus: FetchStatus
  moduleWithProgress: Module[]
  topicData: TopicDetails
  progressionData: OverallProgress
  revisionItems?: TAPIGetAksiNyataRevisionResponse['revisionItems']
  certifiable?: boolean
  certificateStatus?: CertificateStatusType
  appeal: TAPIGetAksiNyataRevisionResponse['appeal']
  refetchProgress: Function
}

const useTopicDetails = (topicId: number | string): GetTopicDetails => {
  const topicDetail = useGetTopicDetail(topicId as string)
  const topicProgress = useGetSingleTopicProgress(
    topicId as string,
    topicDetail.isSuccess
  )
  const aksiNyataRevision = useGetAksiNyataRevision(
    topicId,
    topicProgress.data?.data?.aksiNyata?.state === 'NEED_REVISION'
  )
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle')
  const [moduleWithProgress, setModuleWithProgress] = useState<Module[] | []>(
    []
  )

  const determineBadgeText = (moduleProgression: ModuleProgress) => {
    let badgeText

    if (
      !moduleProgression?.material &&
      moduleProgression?.prerequisiteCompleted
    ) {
      badgeText = 'Pelajari Materi'
    } else if (
      moduleProgression?.material &&
      moduleProgression?.prerequisiteCompleted &&
      moduleProgression?.postTest !== 'passed'
    ) {
      badgeText =
        moduleProgression?.postTest === 'not_started'
          ? 'Kerjakan Post Test'
          : 'Kerjakan Ulang Post Test'
    }

    return badgeText
  }

  /**
   * For modules, progression data is a hard requirement
   * to determine if the module is accessible
   * so first we try to fetch the module list which is inside the topic details
   * and then if succeeded, fetch the module's progression data
   * bail out if any of the query error
   * and set the error status accordingly
   */
  useEffect(() => {
    if (
      topicDetail.isLoading ||
      topicProgress.isLoading ||
      aksiNyataRevision.isLoading
    ) {
      setFetchStatus('loading')
    }

    if (
      topicDetail.isError ||
      topicProgress.isError ||
      aksiNyataRevision.isError
    ) {
      setFetchStatus('error')
    }

    if (topicDetail.isSuccess && topicProgress.isSuccess) {
      if (topicProgress.data?.data?.aksiNyata?.state === 'NEED_REVISION') {
        if (aksiNyataRevision.isSuccess) setFetchStatus('success')
      } else {
        setFetchStatus('success')
      }
    }
  }, [
    topicDetail.isLoading,
    topicDetail.isError,
    topicDetail.isSuccess,
    topicProgress.isLoading,
    topicProgress.isError,
    topicProgress.isSuccess,
    topicProgress.data?.data?.aksiNyata?.state,
    aksiNyataRevision.isLoading,
    aksiNyataRevision.isError,
    aksiNyataRevision.isSuccess,
  ])

  useEffect(() => {
    if (topicDetail.isSuccess && topicProgress.isSuccess) {
      setModuleWithProgress(() =>
        topicDetail.data?.data.modules.map((module) => {
          const moduleProgression =
            topicProgress.data.data?.modules.find(
              (progress) => progress.moduleId === module.id
            ) ?? undefined

          return {
            ...module,
            progress: moduleProgression,
            badgeText: determineBadgeText(moduleProgression),
          }
        })
      )
    }
  }, [
    topicDetail.isSuccess,
    topicDetail.data?.data.modules,
    topicProgress.isSuccess,
    topicProgress.data?.data?.modules,
  ])
  return {
    fetchStatus,
    moduleWithProgress,
    topicData: topicDetail.data?.data,
    progressionData: topicProgress.data?.data,
    revisionItems: aksiNyataRevision.data?.data?.revisionItems,
    certifiable: topicProgress.data?.meta?.certifiable,
    certificateStatus: topicProgress.data?.meta?.certificateStatus,
    appeal:
      aksiNyataRevision.data?.data?.appeal ??
      ({} as TAPIGetAksiNyataRevisionResponse['appeal']),
    refetchProgress: topicProgress.refetch,
  }
}

export default useTopicDetails
